import { useEffect, useState } from 'react';
import { loadShellEventCheck } from './shellBridge';
import { isShellRuntime } from './shellDetection';

/**
 * I-2453: the app shell's runtime (`ShellRuntime.tsx`: its messages, events, tab bar and badges) runs
 * only inside the iOS and Android app, so it is a chunk of its own rather than part of the entry every
 * browser and the desktop app load. Inside the shell the launch awaits it before the first render
 * (`index.tsx`), so it is mounted with the first frame, as it was when it was in the entry.
 *
 * S3 (SBS-8): the check of the shell's events comes with it, so the events the shell sends at launch
 * are checked and delivered by the first render; a browser loads neither.
 */
type ShellRuntimeModuleType = typeof import('./ShellRuntime');

let loaded: ShellRuntimeModuleType | null = null;
let loading: Promise<ShellRuntimeModuleType> | null = null;

export function loadShellRuntime(): Promise<ShellRuntimeModuleType> {
  loading ??= Promise.all([import('./ShellRuntime'), loadShellEventCheck()]).then(
    ([module]) => (loaded = module),
    (error: unknown) => {
      // A failed chunk is asked for again by the next caller rather than remembered.
      loading = null;
      throw error;
    },
  );
  return loading;
}

function ShellRuntimeLoaded() {
  const [module, setModule] = useState(loaded);
  useEffect(() => {
    if (module) return;
    let live = true;
    loadShellRuntime().then(
      (next) => {
        if (live) setModule(next);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [module]);
  const InShell = (module ?? loaded)?.ShellRuntimeInShell;
  return InShell ? <InShell /> : null;
}

/** T-0320: the app shell's messages and events. Renders nothing, and nothing loads in a browser. */
export function ShellRuntime() {
  if (!isShellRuntime()) return null;
  return <ShellRuntimeLoaded />;
}
